import { Subject, takeUntil } from 'rxjs'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	InputSignal,
	OnChanges,
	OnDestroy,
	Signal,
	signal,
	SimpleChanges,
	WritableSignal,
	inject,
} from '@angular/core'
import { CompanyInfoInterface } from '@modules/twelve-data-module/interfaces/company-info.interface'
import { TwelveDataService } from '@modules/twelve-data-module/services/twelve-data.service'
import { TuiSkeleton } from '@taiga-ui/kit'
import { HttpErrorResponse } from '@angular/common/http'
import { TuiIcon } from '@taiga-ui/core'
import { CompanyLogoComponent } from '@shared/components/common/company-logo/company-logo.component'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import { CompactNumberPipe } from '@shared/pipes/compact-number.pipe'
import { AlertService } from '@shared/services/alert.service'
import { LiveTickerInterface } from '@modules/stocks-module/live-ticker/interfaces/live-ticker.interface'
import { AssetExanteInterface } from '@modules/stocks-module/general/interfaces/asset-exante.interface'
import { StocksService } from '@modules/stocks-module/general/services/stocks.service'

@UseLocales({ ru, en })
@Component({
	selector: 'app-info-stocks-description',
	templateUrl: './info-stocks-description.component.html',
	styleUrl: './info-stocks-description.component.scss',
	imports: [TranslatePipe, TuiSkeleton, TuiIcon, CompanyLogoComponent, CompactNumberPipe],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InfoStocksDescriptionComponent implements OnChanges, OnDestroy {
	protected readonly boolean: typeof Boolean = Boolean

	public liveTicker: InputSignal<LiveTickerInterface | null> = input<LiveTickerInterface | null>(null)

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _twelveDataService: TwelveDataService = inject<TwelveDataService>(TwelveDataService)
	private readonly _alertService: AlertService = inject<AlertService>(AlertService)
	private readonly _translateService: TranslateService = inject<TranslateService>(TranslateService)
	private readonly _stocksService: StocksService = inject<StocksService>(StocksService)

	protected companyInfo: WritableSignal<CompanyInfoInterface | null> = signal<CompanyInfoInterface | null>(null)
	protected isLoadingCompanyInfo: WritableSignal<boolean> = signal<boolean>(false)
	protected assetsInfo: WritableSignal<AssetExanteInterface | null> = signal<AssetExanteInterface | null>(null)
	protected isLoadingAssetInfo: WritableSignal<boolean> = signal<boolean>(false)

	protected fullLocation: Signal<string> = computed<string>((): string => {
		const info: CompanyInfoInterface | null = this.companyInfo()
		if (!info) return '-'

		const fullAddress: string[] = [info.state, info.city, info.country]
		const fullLocation: string[] = fullAddress.filter(this.boolean)

		return fullLocation.length ? fullLocation.join(', ') : '-'
	})
	private _loadCompanyInfo(): void {
		const liveTicker: LiveTickerInterface | null = this.liveTicker()
		if (!liveTicker) return

		this.isLoadingCompanyInfo.set(true)

		this._twelveDataService
			.getCompanyInfo(liveTicker.ticker)
			.pipe(takeUntil(this._destroy$))

			.subscribe(
				(response: CompanyInfoInterface): void => {
					this.companyInfo.set(response)
					this.isLoadingCompanyInfo.set(false)
				},
				(error: HttpErrorResponse): void => {
					this._alertService.error(
						error?.error?.error?.message || this._translateService.instant('info.stocks.description.error'),
					)
					this.isLoadingCompanyInfo.set(false)
				},
			)
	}

	private _getAssetsInfo(): void {
		const liveTicker: LiveTickerInterface | null = this.liveTicker()
		if (!liveTicker) return

		this.isLoadingAssetInfo.set(true)

		this._stocksService
			.getExanteAsset(liveTicker.ticker)
			.pipe(takeUntil(this._destroy$))
			.subscribe(
				(assets: AssetExanteInterface): void => {
					this.assetsInfo.set(assets)
					this.isLoadingAssetInfo.set(false)
				},
				(error: HttpErrorResponse): void => {
					this._alertService.error(
						error?.error?.error?.message || this._translateService.instant('orders.assetsInfoFailed'),
					)
					this.isLoadingAssetInfo.set(false)
				},
			)
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!changes['liveTicker']) return

		const currentTicker: string | undefined = changes['liveTicker'].currentValue?.ticker
		const previousTicker: string | undefined = changes['liveTicker'].previousValue?.ticker

		if (currentTicker !== previousTicker) {
			this._loadCompanyInfo()
			this._getAssetsInfo()
		}
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
